/**
 * "Também publicado por": every other publication -- post, reel or story,
 * from any tracked account, this one's own marked "mesma conta" -- whose media matches this one's, regardless of
 * who posted or shared first (platform design §16). One component for every
 * publication kind's page, reusing the account row and sort toggle other
 * screens already use rather than a reshares-only list.
 */

import { Link } from '@tanstack/react-router';
import { User } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { QueryStates } from '../../../chrome/state';
import { Avatar, AvatarFallback, AvatarImage } from '../../../ui/avatar';
import { Badge } from '../../../ui/badge';
import { Skeleton } from '../../../ui/skeleton';
import { SEGMENTED_ITEM_CLASSNAME, ToggleGroup, ToggleGroupItem } from '../../../ui/toggle-group';
import { KIND_ICONS } from '../publications/cells';
import { kindLabel } from '../publications/messages';
import { publicationPath } from '../publications/paths';
import { moment } from '../publications/cells';
import { pub } from './messages';
import { useSharedMedia, type SharedMediaOrder, type SharedMediaRow } from './sharedMedia';

/** "@handle", however the wire spelled it. */
function atHandle(handle: string | null): string | null {
  return handle === null ? null : `@${handle.replace(/^@/, '')}`;
}

function SectionLabel(props: { children: ReactNode }): ReactNode {
  return <h2 className="text-caption text-muted-foreground font-medium">{props.children}</h2>;
}

function SharedMediaSort(props: {
  value: SharedMediaOrder;
  onChange: (order: SharedMediaOrder) => void;
}): ReactNode {
  return (
    <ToggleGroup
      type="single"
      spacing={1}
      aria-label={pub('sharedMedia.sort.label')}
      value={props.value}
      onValueChange={(next) => {
        // Radix reports an empty string when the selected item is pressed
        // again; a segmented selector always has one option chosen.
        if (next !== '') props.onChange(next as SharedMediaOrder);
      }}
      className="bg-secondary rounded-pill p-1"
    >
      <ToggleGroupItem value="recent" className={SEGMENTED_ITEM_CLASSNAME}>
        {pub('sharedMedia.sort.recent')}
      </ToggleGroupItem>
      <ToggleGroupItem value="reach" className={SEGMENTED_ITEM_CLASSNAME}>
        {pub('sharedMedia.sort.reach')}
      </ToggleGroupItem>
    </ToggleGroup>
  );
}

function SharedMediaRowItem(props: { campaignId: string; row: SharedMediaRow }): ReactNode {
  const { row } = props;
  const handle = atHandle(row.handle);
  const name = row.label ?? handle ?? pub('post.authorAbsent');
  const Icon = KIND_ICONS[row.kind];
  return (
    <li>
      <Link
        to={publicationPath(props.campaignId, row.post_id)}
        aria-label={pub(row.same_account ? 'sharedMedia.openSameAccount' : 'sharedMedia.open', {
          name,
        })}
        className="hover:bg-muted focus-visible:ring-ring -mx-2 flex items-center gap-3 rounded-md px-2 py-1.5 outline-none focus-visible:ring-3"
      >
        <Avatar size="sm" aria-hidden>
          {row.avatar_url !== null && <AvatarImage src={row.avatar_url} alt="" />}
          <AvatarFallback>
            {row.label === null && row.handle === null ? (
              <User aria-hidden className="size-4" />
            ) : (
              name.replace(/^@/, '').slice(0, 2).toUpperCase()
            )}
          </AvatarFallback>
        </Avatar>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="text-body-sm truncate">{name}</span>
          {row.label !== null && handle !== null && (
            <span className="text-caption text-muted-foreground truncate">{handle}</span>
          )}
        </span>
        {row.same_account && (
          <Badge variant="secondary" className="shrink-0">
            {pub('sharedMedia.sameAccount')}
          </Badge>
        )}
        <Badge variant="outline" className="shrink-0">
          {Icon !== undefined && <Icon aria-hidden />}
          {kindLabel(row.kind)}
        </Badge>
        <span className="text-caption text-muted-foreground shrink-0">
          {moment(row.published_at)}
        </span>
      </Link>
    </li>
  );
}

export function SharedMediaSection(props: {
  campaignId: string;
  tenantId: string;
  postId: string;
}): ReactNode {
  const [order, setOrder] = useState<SharedMediaOrder>('recent');
  const query = useSharedMedia(props.campaignId, props.tenantId, props.postId, order);
  return (
    <section className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <SectionLabel>{pub('sharedMedia.title')}</SectionLabel>
        <SharedMediaSort value={order} onChange={setOrder} />
      </div>
      <QueryStates
        query={query}
        isEmpty={(data) => data.rows.length === 0}
        loading={<Skeleton className="h-10 w-full" />}
        empty={<p className="text-body-sm text-muted-foreground">{pub('sharedMedia.empty')}</p>}
      >
        {(data) => (
          <ul className="flex flex-col">
            {data.rows.map((row) => (
              <SharedMediaRowItem key={row.post_id} campaignId={props.campaignId} row={row} />
            ))}
          </ul>
        )}
      </QueryStates>
    </section>
  );
}
